import React from 'react';

import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';

import { PATH } from 'src/features/navigation/utils/constants';
import { useCreatePricingPlanVersion } from 'src/features/pricingPlan/api/useCreatePricingPlanVersion';
import { LIST_PRICING_PLANS_RESOURCE } from 'src/features/pricingPlan/api/useListPricingPlans';
import PricingPlanFormModal from 'src/features/pricingPlan/components/form/PricingPlanFormModal';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { PricingPlan } from 'src/features/pricingPlan/utils/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';

interface PricingPlanFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedPricingPlan?: PricingPlan;
    children?: React.ReactNode;
}

function CreatePricingPlanVersionFormModal<TData>({
    className,
    semantics,
    selectedPricingPlan,
}: PricingPlanFormModalProps<TData>) {
    const { formatMessage, locale } = useIntl();
    const { planId = '' } = useParams();
    const { parkingId } = useParking();
    const navigate = useNavigate();

    const { mutate, isPending } = useCreatePricingPlanVersion({
        onSuccess: (success) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_PRICING_PLANS_RESOURCE] });
            toast({
                title: formatMessage(pricingPlanMessages.createPricingPlanVersionSuccess),
                description: formatMessage(pricingPlanMessages.createPricingPlanVersionSuccessDescription, {
                    entry: success.translations.find((translation) => translation.language === locale)?.name,
                }),
            });
            // Land the user on the new version's dashboard.
            void navigate(`/${PATH.ADMINISTRATION}/pricing-plan/${success.id}`);
        },
    });

    return (
        <PricingPlanFormModal
            className={className}
            selectedPricingPlan={selectedPricingPlan}
            mutate={(values, onSuccess, onError) =>
                mutate({ id: planId, parkingId, ...values }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.createEntry, {
                entry: semantics?.entryName?.toLowerCase(),
            })}
            icon={ICON_MAP.ADD}
            isPending={isPending}
            subtitle={formatMessage(pricingPlanMessages.addPricingPlanVersionSubtitle)}
            isNewVersion={true}
        />
    );
}

export default CreatePricingPlanVersionFormModal;
